import React, { useState, useEffect } from "react";
import { Col } from "antd";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  Scale,
  Heart,
  Wallet,
  Code,
  TrendingUp,
  PenTool,
  GraduationCap,
  Palette,
  Mail,
  Rocket,
  Gift,
  Zap,
} from "lucide-react";
import { SpaceBackground } from "@components";

// Browser & platform icons (PNG from uxwing.com)
import chromeLogo from "@assets/images/browser/chrome.png";
import firefoxLogo from "@assets/images/browser/firefox.png";
import safariLogo from "@assets/images/browser/safari.png";
import edgeLogo from "@assets/images/browser/edge.png";
import braveLogo from "@assets/images/browser/brave.png";
import appleLogo from "@assets/images/browser/apple.png";
import androidLogo from "@assets/images/browser/android.png";

const LeftContent = () => {
  const [activeCopilot, setActiveCopilot] = useState(0);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const copilots = [
    { icon: Scale, name: "Legal Copilot", color: "#94a3b8" },
    { icon: Heart, name: "Wellness Copilot", color: "#f87171" },
    { icon: Wallet, name: "Finance Copilot", color: "#34d399" },
    { icon: Code, name: "Code Copilot", color: "#60a5fa" },
    { icon: TrendingUp, name: "Marketing Copilot", color: "#f472b6" },
    { icon: PenTool, name: "Writing Copilot", color: "#fb923c" },
    { icon: GraduationCap, name: "Learning Copilot", color: "#fbbf24" },
    { icon: Palette, name: "Design Copilot", color: "#a78bfa" },
  ];

  const steps = [
    { icon: Mail, title: "Check Email" },
    { icon: Zap, title: "One Click" },
    { icon: Rocket, title: "Launch" },
    { icon: Gift, title: "Free Credits" },
  ];

  const browsers = [
    { logo: chromeLogo, name: "Chrome" },
    { logo: firefoxLogo, name: "Firefox" },
    { logo: safariLogo, name: "Safari" },
    { logo: edgeLogo, name: "Edge" },
    { logo: braveLogo, name: "Brave" },
  ];

  const testimonials = [
    { quote: "Activation was instant. Started working with AI in 2 minutes!", role: "Founder", company: "LaunchPad" },
    { quote: "Love the free credits. Got real value from day one.", role: "Marketer", company: "GrowthCo" },
    { quote: "Smoothest onboarding I've experienced. Well done!", role: "Designer", company: "PixelPerfect" },
  ];

  useEffect(() => {
    const copilotInterval = setInterval(() => {
      setActiveCopilot((prev) => (prev + 1) % copilots.length);
    }, 2000);
    const testimonialInterval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 4000);
    return () => {
      clearInterval(copilotInterval);
      clearInterval(testimonialInterval);
    };
  }, []);

  return (
    <Col
      lg={16}
      md={0}
      sm={0}
      xs={0}
      style={{
        height: "100vh",
        background: "#0a0612",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <SpaceBackground />

      <div
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          zIndex: 1,
          padding: "16px 32px",
        }}
      >
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: "20px" }}>

          {/* Main Headline */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            style={{ textAlign: "center" }}
          >
            <h1
              style={{
                color: "#fff",
                fontSize: "38px",
                fontWeight: 700,
                lineHeight: 1.15,
                margin: "0 0 8px 0",
                letterSpacing: "-0.5px",
              }}
            >
              You're Almost{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, #a78bfa 0%, #c4b5fd 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Ready to Launch
              </span>
            </h1>
            <p
              style={{
                color: "rgba(255,255,255,0.7)",
                fontSize: "15px",
                lineHeight: 1.4,
                margin: "0 auto",
                maxWidth: "400px",
              }}
            >
              Check your inbox for the activation link. Your AI copilots are waiting.
            </p>
          </motion.div>

          {/* AI Copilots - Single Row */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            style={{ textAlign: "center" }}
          >
            <p
              style={{
                color: "rgba(255,255,255,0.5)",
                fontSize: "9px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "1.2px",
                margin: "0 0 8px 0",
              }}
            >
              Your Copilots Await
            </p>
            <div style={{ display: "flex", justifyContent: "center", gap: "6px", flexWrap: "wrap", maxWidth: "520px", margin: "0 auto" }}>
              {copilots.map((copilot, idx) => {
                const Icon = copilot.icon;
                const isActive = idx === activeCopilot;
                return (
                  <motion.div
                    key={idx}
                    animate={{
                      scale: isActive ? 1.08 : 1,
                      y: isActive ? -2 : 0,
                    }}
                    transition={{ duration: 0.2 }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      padding: "6px 12px",
                      background: isActive ? `linear-gradient(135deg, ${copilot.color}30 0%, ${copilot.color}10 100%)` : "rgba(255,255,255,0.03)",
                      borderRadius: "16px",
                      border: isActive ? `1px solid ${copilot.color}60` : "1px solid rgba(255,255,255,0.06)",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={() => setActiveCopilot(idx)}
                  >
                    <Icon
                      size={14}
                      color={isActive ? copilot.color : "rgba(255,255,255,0.6)"}
                      style={{ transition: "color 0.2s ease" }}
                    />
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: 500,
                        color: isActive ? copilot.color : "rgba(255,255,255,0.5)",
                        transition: "color 0.2s ease",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {copilot.name}
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Activation Steps - Compact Pills */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
              {steps.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={idx}
                    whileHover={{ scale: 1.05, y: -1 }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "10px 14px",
                      background: "rgba(167, 139, 250, 0.1)",
                      borderRadius: "20px",
                      border: "1px solid rgba(167, 139, 250, 0.2)",
                      cursor: "pointer",
                      backdropFilter: "blur(8px)",
                      WebkitBackdropFilter: "blur(8px)",
                    }}
                  >
                    <Icon size={14} color="#a78bfa" />
                    <span style={{ color: "#fff", fontSize: "13px", fontWeight: 500 }}>
                      {step.title}
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Platforms Section */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
            style={{ textAlign: "center" }}
          >
            <p style={{
              color: "rgba(255,255,255,0.4)",
              fontSize: "9px",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "1px",
              margin: "0 0 6px 0",
            }}>
              Available Everywhere
            </p>

            <div style={{ display: "flex", justifyContent: "center", gap: "6px", marginBottom: "8px" }}>
              {browsers.map((browser, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ scale: 1.1, y: -1 }}
                  style={{
                    width: "38px",
                    height: "38px",
                    borderRadius: "6px",
                    background: "rgba(255,255,255,0.95)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                  }}
                  title={browser.name}
                >
                  <img src={browser.logo} alt={browser.name} style={{ width: 22, height: 22 }} />
                </motion.div>
              ))}
            </div>

            {/* App Store Buttons */}
            <div style={{ display: "flex", justifyContent: "center", gap: "6px" }}>
              <motion.div
                whileHover={{ scale: 1.05 }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "8px 14px",
                  background: "rgba(0,0,0,0.6)",
                  borderRadius: "6px",
                  cursor: "pointer",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <img src={appleLogo} alt="Apple" style={{ width: 14, height: 14, filter: "brightness(0) invert(1)" }} />
                <span style={{ fontSize: "11px", fontWeight: 600, color: "#fff" }}>App Store</span>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "8px 14px",
                  background: "rgba(0,0,0,0.6)",
                  borderRadius: "6px",
                  cursor: "pointer",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <img src={androidLogo} alt="Android" style={{ width: 14, height: 14 }} />
                <span style={{ fontSize: "11px", fontWeight: 600, color: "#fff" }}>Google Play</span>
              </motion.div>
            </div>
          </motion.div>

          {/* Testimonial - Smaller */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.4 }}
            style={{
              background: "rgba(167, 139, 250, 0.08)",
              borderRadius: "12px",
              padding: "16px 24px",
              border: "1px solid rgba(167, 139, 250, 0.15)",
              width: "100%",
              maxWidth: "420px",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
            }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTestimonial}
                initial={{ opacity: 0, x: 15 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -15 }}
                transition={{ duration: 0.25 }}
                style={{ textAlign: "center" }}
              >
                <div style={{ display: "flex", gap: "2px", marginBottom: "6px", justifyContent: "center" }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span key={star} style={{ color: "#fbbf24", fontSize: "11px" }}>★</span>
                  ))}
                </div>
                <p style={{ color: "#fff", fontSize: "14px", fontWeight: 500, margin: "0 0 6px 0", lineHeight: 1.35 }}>
                  "{testimonials[activeTestimonial].quote}"
                </p>
                <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "13px", margin: 0 }}>
                  {testimonials[activeTestimonial].role} at{" "}
                  <span style={{ color: "#a78bfa" }}>{testimonials[activeTestimonial].company}</span>
                </p>
              </motion.div>
            </AnimatePresence>
            <div style={{ display: "flex", gap: "4px", marginTop: "10px", justifyContent: "center" }}>
              {testimonials.map((_, idx) => (
                <div
                  key={idx}
                  style={{
                    width: idx === activeTestimonial ? "16px" : "5px",
                    height: "5px",
                    borderRadius: "3px",
                    background: idx === activeTestimonial ? "#a78bfa" : "rgba(255,255,255,0.2)",
                    transition: "all 0.3s ease",
                  }}
                />
              ))}
            </div>
          </motion.div>

          {/* Stats - Smaller */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.5 }}
            style={{
              display: "flex",
              gap: "32px",
              justifyContent: "center",
            }}
          >
            {[
              { value: "2 min", label: "To first result" },
              { value: "Free", label: "Credits included" },
              { value: "50K+", label: "Users" },
              { value: "4.9/5", label: "Rating" },
            ].map((stat, idx) => (
              <div key={idx} style={{ textAlign: "center" }}>
                <div
                  style={{
                    fontSize: "22px",
                    fontWeight: 700,
                    color: "#fff",
                    lineHeight: 1,
                    marginBottom: "2px",
                  }}
                >
                  {stat.value}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "rgba(255,255,255,0.5)",
                    fontWeight: 500,
                  }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.6 }}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "6px",
            paddingTop: "12px",
            borderTop: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Sparkles size={14} color="#a78bfa" />
          <span style={{ color: "rgba(255,255,255,0.6)", fontSize: "12px" }}>
            Powered by GPT, Claude, Grok, Qwen, DeepSeek & more
          </span>
        </motion.div>
      </div>
    </Col>
  );
};

export default LeftContent;
